import EventHandler from '@pixelunion/events';
import ComplementaryProducts from '@pixelunion/pxs-complementary-products';
import { transition } from '@pixelunion/animations';
import Rte from '../utils/Rte';
import PaymentTerms from '../helpers/PaymentTerms';
import ProductDetails from '../components/ProductDetails';
import ProductGallery from '../components/ProductGallery';

function updateCart() {
  window.Shopify.getCart(cart => {
    // Update cart text
    const cartAmountWrap = document.querySelector('[data-header-cart-count]');

    if (cartAmountWrap) {
      cartAmountWrap.innerHTML = `(${cart.item_count})`;
    }
  });
}

export default class Product {
  constructor(section) {
    this.events = new EventHandler();
    this.el = section.el;
    this.sectionId = section.id;
    this.data = section.data;
    this.product = this.data.product;

    this.addingToCart = false;

    this.responseMessage = this.el.querySelector('[data-response-message]');
    this.responseMessageAnimation = transition({ el: this.responseMessage, state: 'closed' });

    this.productForm = this.el.querySelector('[data-product-form]');
    this.recipientForm = this.productForm.querySelector('[data-recipient-form]');
    this.addToCartButton = this.productForm.querySelector('[data-product-atc]');

    const complementaryProductsEl = this.el.querySelector('[data-complementary-products]');
    if (complementaryProductsEl) {
      this.complementaryProducts = new ComplementaryProducts({
        sectionEl: this.el,
        sectionId: section.id,
        productId: this.product.id,
        productRecommendationsRoute: window.Theme.routes.product_recommendations_url,
        includeIndicatorDots: true,
        limit: this.data.complementary_product_limit,
      });
    }

    this.gallery = new ProductGallery(this.el.querySelector('[data-product-gallery]'), {
      enable_image_zoom: this.data.gallery_image_zoom,
      enable_video_autoplay: this.data.gallery_video_autoplay,
      enable_video_looping: this.data.gallery_video_looping,
    });

    this.productDetails = new ProductDetails({
      el: this.el,
      product: this.product,
      settings: this.data,
      onVariantChange: ({ variant }) => this.gallery.selectMediaByVariant(variant),
    });

    this.rtes = [];

    this.el.querySelectorAll('[data-rich-text-area]').forEach(el => this.rtes.push(new Rte({ el })));

    window.Shopify.onError = XMLHttpRequest => this.handleErrors(XMLHttpRequest);
    this.events.register(this.addToCartButton, 'click', event => this.addToCart(event));

    this.paymentTerms = new PaymentTerms(this.el);
  }

  addToCart(e) {
    if (this.data.enable_cart_redirection || document.querySelector('.template-cart')) {
      return;
    }

    e.preventDefault();

    // Animate the response message.
    this.responseMessageAnimation.animateTo('closed');

    if (this.addingToCart) return;

    const data = new FormData(this.productForm);

    if (this.recipientForm) {
      this.recipientForm.classList.remove('recipient-form--has-errors');
    }

    if (!this.addToCartButton.disabled) {
      this.addingToCart = true;
      this.addToCartButton.classList.add('product-details__add-to-cart-button--loading');

      fetch(window.Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          Accept: 'application/javascript',
        },
        body: data,
      })
        .then(response => response.json())
        .then(response => {
          if (response && response.status) {
            return this.handleErrors(response);
          }

          const message = this.data.atc_message_success
            .replace('{{ product }}', `${this.product.title}`)
            .replace('{{ cart_link }}', `<a href="${window.Theme.routes.cart_url}">${this.data.atc_cart_link}</a>`)
            .replace('{{ continue_link }}', `<a href="${window.Theme.routes.all_products_collection_url}">${this.data.atc_continue_shopping}</a>`)
            .replace('{{ checkout_link }}', `<form class="product-message__form" action="${window.Theme.routes.cart_url}" method="POST"><button class="product-message__btn" type="submit" name="checkout">${this.data.atc_checkout_link}</button></form>`);

          setTimeout(() => {
            // Remove the loading state from the "Add to cart" button.
            this.addToCartButton.classList.remove('product-details__add-to-cart-button--loading');

            // Animate and insert the response message.
            this.responseMessageAnimation.animateTo('open');
            this.responseMessage.innerHTML = message;

            this.addingToCart = false;

            updateCart();
          },
          500);
        })
        .catch(error => {
          console.error('Error fetching data:', error);
        });
    }
  }

  handleErrors(response) {
    setTimeout(() => {
      // Remove the loading state from the "Add to cart" button.
      this.addToCartButton.classList.remove('product-details__add-to-cart-button--loading');

      // Animate and style the error response message.
      this.responseMessageAnimation.animateTo('open');
      this.responseMessage.classList.add('response-message--has-errors');

      // Generate error messages.
      let errorMessage = this.data.generic_error_message;

      if (response.status === 401) {
        errorMessage = `${errorMessage} ${this.data.error_message_unauthorized}`;
      } else {
        errorMessage = response.description;
      }

      // Insert error message (excluding recipient form).
      if (this.recipientForm) {
        this.recipientForm.classList.add('recipient-form--has-errors');
      } else {
        this.responseMessage.innerHTML = errorMessage;
      }

      this.addingToCart = false;
    },
    1000);
  }

  unload() {
    this.events.unregisterAll();
    this.productDetails.unload();
    this.rtes.forEach(rte => rte.unload());
  }

  onSectionUnload() {
    this.unload();
  }
}
